import React, { useState, useCallback, useRef, useEffect } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Plus, Type, Image, MousePointerClick, Minus, ArrowUpDown, Tag, Heading1, Bold, Italic, Underline, Link2, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Strikethrough, Quote, Code, Undo2, Redo2, Sparkles, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const BLOCK_TYPES = [
  { type: 'header', label: 'Header', icon: Heading1 },
  { type: 'text', label: 'Text', icon: Type },
  { type: 'image', label: 'Image', icon: Image },
  { type: 'button', label: 'Button', icon: MousePointerClick },
  { type: 'divider', label: 'Divider', icon: Minus },
  { type: 'spacer', label: 'Spacer', icon: ArrowUpDown },
  { type: 'coupon', label: 'Coupon', icon: Tag },
];

const defaultBlockData = (type) => {
  switch (type) {
    case 'header': return { title: 'APOLLO SHADES', subtitle: 'Premium Window Solutions', bgColor: '#171717', titleColor: '#ffffff', subtitleColor: '#d97706' };
    case 'text': return { content: 'Enter your text here...', color: '#444444', fontSize: '15' };
    case 'image': return { url: '', alt: 'Image', width: '100' };
    case 'button': return { text: 'Shop Now', href: 'https://apolloshades.com', bgColor: '#d97706', textColor: '#ffffff', align: 'left' };
    case 'divider': return { color: '#e5e5e5', thickness: '1' };
    case 'spacer': return { height: '24' };
    case 'coupon': return { code: 'SPRING15', description: 'Valid through end of month', label: 'Use code:', bgColor: '#fffbeb', borderColor: '#fcd34d', textColor: '#92400e', codeColor: '#d97706' };
    default: return {};
  }
};

let _blockIdCounter = Date.now();
const nextId = () => `block-${++_blockIdCounter}`;

/* ─── Undo/Redo Hook ─── */
const MAX_HISTORY = 20;
export function useBlockHistory(initialBlocks) {
  const resolvedInit = typeof initialBlocks === 'function' ? initialBlocks() : initialBlocks;
  const [blocks, setBlocksRaw] = useState(resolvedInit);
  const historyRef = useRef([JSON.stringify(resolvedInit)]);
  const indexRef = useRef(0);
  const skipPushRef = useRef(false);

  const setBlocks = useCallback((newBlocks) => {
    const val = typeof newBlocks === 'function' ? newBlocks(blocks) : newBlocks;
    setBlocksRaw(val);
    if (skipPushRef.current) { skipPushRef.current = false; return; }
    try {
      const snap = JSON.stringify(val);
      if (!snap) return;
      // Trim forward history
      historyRef.current = historyRef.current.slice(0, indexRef.current + 1);
      historyRef.current.push(snap);
      if (historyRef.current.length > MAX_HISTORY) historyRef.current.shift();
      indexRef.current = historyRef.current.length - 1;
    } catch { /* ignore serialization errors */ }
  }, [blocks]);

  const undo = useCallback(() => {
    if (indexRef.current <= 0) return;
    indexRef.current--;
    skipPushRef.current = true;
    try {
      setBlocksRaw(JSON.parse(historyRef.current[indexRef.current]));
    } catch { indexRef.current++; }
  }, []);

  const redo = useCallback(() => {
    if (indexRef.current >= historyRef.current.length - 1) return;
    indexRef.current++;
    skipPushRef.current = true;
    try {
      setBlocksRaw(JSON.parse(historyRef.current[indexRef.current]));
    } catch { indexRef.current--; }
  }, []);

  const canUndo = indexRef.current > 0;
  const canRedo = indexRef.current < historyRef.current.length - 1;

  const resetHistory = useCallback((newBlocks) => {
    historyRef.current = [JSON.stringify(newBlocks)];
    indexRef.current = 0;
    setBlocksRaw(newBlocks);
  }, []);

  return { blocks, setBlocks, undo, redo, canUndo, canRedo, resetHistory };
}

/* ─── Snippet Definitions ─── */
const SNIPPETS = [
  {
    id: 'hero_banner',
    label: 'Hero Banner',
    desc: 'Bold header with tagline',
    blocks: () => [
      { id: nextId(), type: 'header', data: { title: 'APOLLO SHADES', subtitle: 'Crafted for your space', bgColor: '#171717', titleColor: '#ffffff', subtitleColor: '#d97706' } },
      { id: nextId(), type: 'spacer', data: { height: '16' } },
    ],
  },
  {
    id: 'cta_button',
    label: 'Call to Action',
    desc: 'Prominent action button',
    blocks: () => [
      { id: nextId(), type: 'button', data: { text: 'Get Your Free Quote', href: 'https://apolloshades.com/contact', bgColor: '#d97706', textColor: '#ffffff', align: 'center' } },
      { id: nextId(), type: 'spacer', data: { height: '12' } },
    ],
  },
  {
    id: 'promo_block',
    label: 'Promo / Discount',
    desc: 'Coupon with intro text',
    blocks: () => [
      { id: nextId(), type: 'text', data: { content: '<b>Exclusive Offer Just for You</b><br/>For a limited time, save on premium roller shades and motorized blinds.', color: '#444444', fontSize: '15' } },
      { id: nextId(), type: 'coupon', data: { code: 'SAVE20', description: 'Valid for 7 days — online & in-store', label: 'Your code:', bgColor: '#fffbeb', borderColor: '#fcd34d', textColor: '#92400e', codeColor: '#d97706' } },
    ],
  },
  {
    id: 'testimonial',
    label: 'Testimonial',
    desc: 'Customer quote block',
    blocks: () => [
      { id: nextId(), type: 'divider', data: { color: '#e5e5e5', thickness: '1' } },
      { id: nextId(), type: 'text', data: { content: '<blockquote>"Apollo Shades transformed our living room. The motorized blinds are incredibly smooth and the fabric quality is outstanding."</blockquote><p style="text-align:right;font-size:13px;color:#888;">— Sarah M., Brooklyn</p>', color: '#444444', fontSize: '15' } },
      { id: nextId(), type: 'divider', data: { color: '#e5e5e5', thickness: '1' } },
    ],
  },
  {
    id: 'product_highlight',
    label: 'Product Spotlight',
    desc: 'Feature a product with CTA',
    blocks: () => [
      { id: nextId(), type: 'text', data: { content: '<b style="font-size:18px;">Motorized Roller Shades</b><br/>Whisper-quiet motors, smartphone control, and premium fabrics — starting at $189.', color: '#333333', fontSize: '15' } },
      { id: nextId(), type: 'button', data: { text: 'View Collection', href: 'https://apolloshades.com/products/motorized-roller-shades', bgColor: '#171717', textColor: '#ffffff', align: 'left' } },
    ],
  },
  {
    id: 'footer_social',
    label: 'Footer + Socials',
    desc: 'Sign-off with social links',
    blocks: () => [
      { id: nextId(), type: 'divider', data: { color: '#e5e5e5', thickness: '1' } },
      { id: nextId(), type: 'text', data: { content: '<p style="text-align:center;font-size:13px;color:#999;">Follow us for inspiration &amp; offers</p><p style="text-align:center;"><a href="https://instagram.com/apolloshades" style="color:#d97706;margin:0 8px;">Instagram</a> · <a href="https://facebook.com/apolloshades" style="color:#d97706;margin:0 8px;">Facebook</a> · <a href="https://pinterest.com/apolloshades" style="color:#d97706;margin:0 8px;">Pinterest</a></p>', color: '#999999', fontSize: '12' } },
    ],
  },
];

/* ─── Sortable Block Wrapper ─── */
function SortableBlock({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="group relative flex items-stretch border border-transparent hover:border-amber-200 rounded-lg transition-colors">
      <div {...attributes} {...listeners} className="flex items-center px-1.5 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity text-neutral-400 hover:text-neutral-600">
        <GripVertical className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

/* ─── Block Editors ─── */
function HeaderEditor({ data, onChange }) {
  return (
    <div className="p-3 rounded-lg" style={{ backgroundColor: data.bgColor }}>
      <input value={data.title} onChange={e => onChange({ ...data, title: e.target.value })} className="w-full bg-transparent text-center font-bold text-xl tracking-widest outline-none" style={{ color: data.titleColor }} />
      <input value={data.subtitle} onChange={e => onChange({ ...data, subtitle: e.target.value })} className="w-full bg-transparent text-center text-xs mt-1 outline-none" style={{ color: data.subtitleColor }} />
      <div className="flex gap-2 mt-2 justify-center">
        <label className="text-[10px] text-neutral-400 flex items-center gap-1">BG <input type="color" value={data.bgColor} onChange={e => onChange({ ...data, bgColor: e.target.value })} className="w-5 h-5 rounded cursor-pointer" /></label>
        <label className="text-[10px] text-neutral-400 flex items-center gap-1">Title <input type="color" value={data.titleColor} onChange={e => onChange({ ...data, titleColor: e.target.value })} className="w-5 h-5 rounded cursor-pointer" /></label>
      </div>
    </div>
  );
}

function TextEditor({ data, onChange }) {
  const editorRef = useRef(null);
  const lastHtmlRef = useRef(data.content || '');

  // Sync external data changes into the contentEditable div
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.innerHTML !== data.content) {
      // Only update DOM when content genuinely changed from outside
      const sel = window.getSelection();
      const hadFocus = document.activeElement === el;
      el.innerHTML = data.content || '';
      lastHtmlRef.current = data.content || '';
      // Restore cursor to end if we had focus
      if (hadFocus && el.childNodes.length) {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  }, [data.content]);

  const syncContent = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML;
    if (html !== lastHtmlRef.current) {
      lastHtmlRef.current = html;
      onChange({ ...data, content: html });
    }
  }, [data, onChange]);

  const execCmd = useCallback((cmd, value = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
    // Sync after command
    setTimeout(() => syncContent(), 0);
  }, [syncContent]);

  const handleLink = useCallback(() => {
    const url = prompt('Enter URL:', 'https://');
    if (url) execCmd('createLink', url);
  }, [execCmd]);

  const toolbarBtns = [
    { cmd: 'bold', icon: Bold, title: 'Bold' },
    { cmd: 'italic', icon: Italic, title: 'Italic' },
    { cmd: 'underline', icon: Underline, title: 'Underline' },
    { cmd: 'strikethrough', icon: Strikethrough, title: 'Strikethrough' },
    { type: 'sep' },
    { cmd: 'insertUnorderedList', icon: List, title: 'Bullet List' },
    { cmd: 'insertOrderedList', icon: ListOrdered, title: 'Numbered List' },
    { type: 'sep' },
    { cmd: 'justifyLeft', icon: AlignLeft, title: 'Align Left' },
    { cmd: 'justifyCenter', icon: AlignCenter, title: 'Align Center' },
    { cmd: 'justifyRight', icon: AlignRight, title: 'Align Right' },
    { type: 'sep' },
    { cmd: 'link', icon: Link2, title: 'Insert Link', handler: handleLink },
    { cmd: 'formatBlock_blockquote', icon: Quote, title: 'Blockquote' },
    { cmd: 'formatBlock_pre', icon: Code, title: 'Code Block' },
  ];

  return (
    <div className="p-3" data-testid="rich-text-editor">
      {/* Formatting Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-1.5 bg-neutral-100 border border-b-0 rounded-t-md" data-testid="rich-text-toolbar">
        {toolbarBtns.map((btn, i) => {
          if (btn.type === 'sep') return <div key={i} className="w-px h-5 bg-neutral-300 mx-0.5" />;
          const Icon = btn.icon;
          return (
            <button
              key={btn.cmd}
              type="button"
              title={btn.title}
              onMouseDown={e => {
                e.preventDefault(); // prevent losing focus from contentEditable
                if (btn.handler) { btn.handler(); return; }
                if (btn.cmd.startsWith('formatBlock_')) {
                  const tag = btn.cmd.split('_')[1];
                  execCmd('formatBlock', `<${tag}>`);
                } else {
                  execCmd(btn.cmd);
                }
              }}
              className="p-1.5 rounded hover:bg-white hover:shadow-sm text-neutral-500 hover:text-neutral-800 transition-colors"
              data-testid={`toolbar-${btn.cmd}`}
            >
              <Icon className="w-3.5 h-3.5" />
            </button>
          );
        })}
      </div>

      {/* ContentEditable Area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={syncContent}
        onBlur={syncContent}
        className="w-full border border-t-0 rounded-b-md p-3 text-sm min-h-[80px] max-h-[300px] overflow-y-auto focus:ring-1 focus:ring-amber-300 outline-none prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-4 [&_blockquote]:border-amber-300 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-neutral-500 [&_pre]:bg-neutral-100 [&_pre]:p-2 [&_pre]:rounded [&_pre]:text-xs [&_pre]:font-mono [&_a]:text-amber-600 [&_a]:underline"
        style={{ color: data.color, fontSize: `${data.fontSize}px` }}
        data-testid="rich-text-content"
      />

      {/* Style Controls */}
      <div className="flex gap-3 mt-1.5">
        <label className="text-[10px] text-neutral-400 flex items-center gap-1">Color <input type="color" value={data.color} onChange={e => onChange({ ...data, color: e.target.value })} className="w-5 h-5 rounded cursor-pointer" /></label>
        <label className="text-[10px] text-neutral-400 flex items-center gap-1">Size <input type="number" value={data.fontSize} onChange={e => onChange({ ...data, fontSize: e.target.value })} className="w-12 border rounded px-1 text-xs" min="10" max="32" />px</label>
      </div>
    </div>
  );
}

function ImageEditor({ data, onChange }) {
  return (
    <div className="p-3 space-y-2">
      <Input value={data.url} onChange={e => onChange({ ...data, url: e.target.value })} placeholder="Image URL (https://...)" className="text-sm" />
      {data.url && <img src={data.url} alt={data.alt} className="max-h-32 rounded border mx-auto" style={{ width: `${data.width}%` }} />}
      <div className="flex gap-3">
        <label className="text-[10px] text-neutral-400 flex items-center gap-1">Width <input type="range" min="20" max="100" value={data.width} onChange={e => onChange({ ...data, width: e.target.value })} className="w-20" />{data.width}%</label>
        <Input value={data.alt} onChange={e => onChange({ ...data, alt: e.target.value })} placeholder="Alt text" className="text-xs h-6 w-32" />
      </div>
    </div>
  );
}

function ButtonEditor({ data, onChange }) {
  return (
    <div className="p-3 space-y-2">
      <div className="flex gap-2">
        <Input value={data.text} onChange={e => onChange({ ...data, text: e.target.value })} placeholder="Button text" className="text-sm" />
        <Input value={data.href} onChange={e => onChange({ ...data, href: e.target.value })} placeholder="Link URL" className="text-sm" />
      </div>
      <div className="flex gap-3">
        <label className="text-[10px] text-neutral-400 flex items-center gap-1">BG <input type="color" value={data.bgColor} onChange={e => onChange({ ...data, bgColor: e.target.value })} className="w-5 h-5 rounded cursor-pointer" /></label>
        <label className="text-[10px] text-neutral-400 flex items-center gap-1">Text <input type="color" value={data.textColor} onChange={e => onChange({ ...data, textColor: e.target.value })} className="w-5 h-5 rounded cursor-pointer" /></label>
        <select value={data.align} onChange={e => onChange({ ...data, align: e.target.value })} className="text-xs border rounded px-1">
          <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
        </select>
      </div>
    </div>
  );
}

function DividerEditor({ data, onChange }) {
  return (
    <div className="p-3 flex items-center gap-3">
      <hr className="flex-1" style={{ borderColor: data.color, borderWidth: `${data.thickness}px` }} />
      <label className="text-[10px] text-neutral-400 flex items-center gap-1">Color <input type="color" value={data.color} onChange={e => onChange({ ...data, color: e.target.value })} className="w-5 h-5 rounded cursor-pointer" /></label>
    </div>
  );
}

function SpacerEditor({ data, onChange }) {
  return (
    <div className="p-3 flex items-center gap-2">
      <div className="flex-1 border border-dashed border-neutral-200 rounded flex items-center justify-center text-[10px] text-neutral-300" style={{ height: `${Math.min(data.height, 60)}px` }}>spacer {data.height}px</div>
      <input type="range" min="8" max="80" value={data.height} onChange={e => onChange({ ...data, height: e.target.value })} className="w-24" />
    </div>
  );
}

function CouponEditor({ data, onChange }) {
  return (
    <div className="p-3 rounded-lg border" style={{ backgroundColor: data.bgColor, borderColor: data.borderColor }}>
      <div className="flex gap-2 mb-1">
        <Input value={data.label} onChange={e => onChange({ ...data, label: e.target.value })} className="text-sm h-7 w-28" placeholder="Label" />
        <Input value={data.code} onChange={e => onChange({ ...data, code: e.target.value })} className="text-sm h-7 font-bold" style={{ color: data.codeColor }} placeholder="CODE" />
      </div>
      <Input value={data.description} onChange={e => onChange({ ...data, description: e.target.value })} className="text-xs h-6" placeholder="Description" />
      <div className="flex gap-2 mt-2">
        <label className="text-[10px] text-neutral-400 flex items-center gap-1">BG <input type="color" value={data.bgColor} onChange={e => onChange({ ...data, bgColor: e.target.value })} className="w-4 h-4 rounded cursor-pointer" /></label>
        <label className="text-[10px] text-neutral-400 flex items-center gap-1">Code <input type="color" value={data.codeColor} onChange={e => onChange({ ...data, codeColor: e.target.value })} className="w-4 h-4 rounded cursor-pointer" /></label>
      </div>
    </div>
  );
}

const EDITORS = { header: HeaderEditor, text: TextEditor, image: ImageEditor, button: ButtonEditor, divider: DividerEditor, spacer: SpacerEditor, coupon: CouponEditor };

/* ─── HTML Generation ─── */
function blockToHtml(block) {
  const d = block.data;
  switch (block.type) {
    case 'header':
      return `<div style="background:${d.bgColor};padding:24px 32px;text-align:center;"><h1 style="color:${d.titleColor};font-size:24px;letter-spacing:2px;margin:0;font-family:Georgia,serif;">${esc(d.title)}</h1>${d.subtitle ? `<p style="color:${d.subtitleColor};font-size:13px;margin:8px 0 0;">${esc(d.subtitle)}</p>` : ''}</div>`;
    case 'text': {
      // Content is rich HTML from contentEditable; render directly if it contains tags
      const isRich = /<[a-z][\s\S]*>/i.test(d.content);
      return `<div style="padding:0 32px;"><div style="color:${d.color};font-size:${d.fontSize}px;line-height:1.7;margin:0 0 16px;">${isRich ? d.content : esc(d.content).replace(/\n/g, '<br/>')}</div></div>`;
    }
    case 'image':
      return d.url ? `<div style="padding:12px 32px;text-align:center;"><img src="${esc(d.url)}" alt="${esc(d.alt)}" style="max-width:${d.width}%;height:auto;border-radius:6px;" /></div>` : '';
    case 'button':
      return `<div style="padding:8px 32px;text-align:${d.align};"><a href="${esc(d.href)}" style="display:inline-block;background:${d.bgColor};color:${d.textColor};padding:12px 28px;border-radius:6px;text-decoration:none;font-weight:600;">${esc(d.text)}</a></div>`;
    case 'divider':
      return `<div style="padding:8px 32px;"><hr style="border:none;border-top:${d.thickness}px solid ${d.color};margin:0;" /></div>`;
    case 'spacer':
      return `<div style="height:${d.height}px;"></div>`;
    case 'coupon':
      return `<div style="margin:0 32px 16px;background:${d.bgColor};border:1px solid ${d.borderColor};border-radius:8px;padding:16px;"><p style="font-size:18px;font-weight:700;color:${d.textColor};margin:0;">${esc(d.label)} <span style="color:${d.codeColor};">${esc(d.code)}</span></p><p style="font-size:12px;color:${d.textColor};margin:4px 0 0;">${esc(d.description)}</p></div>`;
    default: return '';
  }
}

function esc(s) { return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }

export function blocksToHtml(blocks) {
  const inner = blocks.map(blockToHtml).filter(Boolean).join('\n');
  return `<div style="font-family:'Segoe UI',Arial,sans-serif;max-width:600px;margin:0 auto;color:#171717;background:#ffffff;border-radius:12px;overflow:hidden;border:1px solid #e7e5e4;">\n${inner}\n<div style="padding:16px 32px;background:#fafaf9;text-align:center;font-size:11px;color:#999;">Apollo Shades &mdash; 1 Brooklyn Navy Yard, Building 33, Suite 201B, Brooklyn, NY 11205 &mdash; (212) 209-0654</div>\n</div>`;
}

/* ─── Template to Blocks ─── */
export function templateToBlocks(templateId) {
  if (templateId === 'seasonal') {
    return [
      { id: nextId(), type: 'header', data: defaultBlockData('header') },
      { id: nextId(), type: 'text', data: { ...defaultBlockData('text'), content: 'We noticed you were exploring shades for your home. For a limited time, enjoy 15% off all roller shades and motorized options.', fontSize: '15' } },
      { id: nextId(), type: 'coupon', data: defaultBlockData('coupon') },
      { id: nextId(), type: 'button', data: { ...defaultBlockData('button'), text: 'Shop Now', href: 'https://apolloshades.com/products/roller-shades' } },
    ];
  }
  if (templateId === 'followup') {
    return [
      { id: nextId(), type: 'header', data: { ...defaultBlockData('header'), subtitle: '' } },
      { id: nextId(), type: 'text', data: { ...defaultBlockData('text'), content: 'You recently built a shade configuration with us. Ready to move forward? Our team is here to help finalize your order and schedule installation at your convenience.', fontSize: '15' } },
      { id: nextId(), type: 'text', data: { ...defaultBlockData('text'), content: 'Reply to this email or call us directly — we\'d love to help bring your vision to life.', fontSize: '15' } },
      { id: nextId(), type: 'button', data: { ...defaultBlockData('button'), text: 'Continue Configuring', bgColor: '#171717', href: 'https://apolloshades.com/products/roller-shades' } },
    ];
  }
  return [{ id: nextId(), type: 'header', data: defaultBlockData('header') }, { id: nextId(), type: 'text', data: defaultBlockData('text') }];
}

/* ─── Main Component ─── */
export default function EmailBlockEditor({ blocks, onChange, onUndo, onRedo, canUndo, canRedo }) {
  const [expandedAdd, setExpandedAdd] = useState(false);
  const [showSnippets, setShowSnippets] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor));

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = blocks.findIndex(b => b.id === active.id);
      const newIndex = blocks.findIndex(b => b.id === over.id);
      onChange(arrayMove(blocks, oldIndex, newIndex));
    }
  }, [blocks, onChange]);

  const addBlock = (type) => {
    onChange([...blocks, { id: nextId(), type, data: defaultBlockData(type) }]);
    setExpandedAdd(false);
  };

  const insertSnippet = (snippet) => {
    onChange([...blocks, ...snippet.blocks()]);
    setShowSnippets(false);
  };

  const updateBlock = (id, data) => {
    onChange(blocks.map(b => b.id === id ? { ...b, data } : b));
  };

  const removeBlock = (id) => {
    onChange(blocks.filter(b => b.id !== id));
  };

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        onUndo?.();
      }
      if ((e.ctrlKey || e.metaKey) && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
        e.preventDefault();
        onRedo?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onUndo, onRedo]);

  return (
    <div className="space-y-1" data-testid="email-block-editor">
      {/* Toolbar: Undo / Redo / Snippets */}
      <div className="flex items-center gap-1 pb-1.5 border-b border-neutral-100 mb-1" data-testid="editor-toolbar">
        <button onClick={onUndo} disabled={!canUndo} className="p-1.5 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Undo (Ctrl+Z)" data-testid="undo-btn">
          <Undo2 className="w-4 h-4" />
        </button>
        <button onClick={onRedo} disabled={!canRedo} className="p-1.5 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Redo (Ctrl+Shift+Z)" data-testid="redo-btn">
          <Redo2 className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-neutral-200 mx-1" />
        <button onClick={() => setShowSnippets(!showSnippets)} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${showSnippets ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'hover:bg-neutral-100 text-neutral-500 hover:text-neutral-700'}`} data-testid="snippets-toggle">
          <Sparkles className="w-3.5 h-3.5" /> Snippets
        </button>
      </div>

      {/* Snippets Panel */}
      {showSnippets && (
        <div className="bg-amber-50/50 border border-amber-100 rounded-lg p-2.5 mb-1" data-testid="snippets-panel">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-amber-800">Insert Snippet</span>
            <button onClick={() => setShowSnippets(false)} className="p-0.5 rounded hover:bg-amber-100 text-amber-400 hover:text-amber-700"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {SNIPPETS.map(s => (
              <button key={s.id} onClick={() => insertSnippet(s)} className="flex flex-col items-start p-2 bg-white rounded-md border border-amber-100 hover:border-amber-300 hover:shadow-sm transition-all text-left" data-testid={`snippet-${s.id}`}>
                <span className="text-xs font-medium text-neutral-700">{s.label}</span>
                <span className="text-[10px] text-neutral-400 leading-tight">{s.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
          {blocks.map(block => {
            const Editor = EDITORS[block.type];
            return (
              <SortableBlock key={block.id} id={block.id}>
                <div className="relative">
                  {Editor && <Editor data={block.data} onChange={(d) => updateBlock(block.id, d)} />}
                  <button onClick={() => removeBlock(block.id)} className="absolute top-1 right-1 p-1 rounded-md text-neutral-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-block-${block.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </SortableBlock>
            );
          })}
        </SortableContext>
      </DndContext>

      {/* Add Block */}
      <div className="pt-2">
        {expandedAdd ? (
          <div className="flex flex-wrap gap-1.5 p-2 bg-neutral-50 rounded-lg border border-dashed border-neutral-200" data-testid="block-type-picker">
            {BLOCK_TYPES.map(bt => (
              <button key={bt.type} onClick={() => addBlock(bt.type)} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border rounded-md text-xs font-medium text-neutral-600 hover:border-amber-300 hover:text-amber-700 transition-colors" data-testid={`add-block-${bt.type}`}>
                <bt.icon className="w-3.5 h-3.5" /> {bt.label}
              </button>
            ))}
            <button onClick={() => setExpandedAdd(false)} className="px-2.5 py-1.5 text-xs text-neutral-400 hover:text-neutral-600">Cancel</button>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setExpandedAdd(true)} className="w-full border-dashed" data-testid="add-block-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Block
          </Button>
        )}
      </div>
    </div>
  );
}
